import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTeachersForCourse, removeTeacherFromCourse } from '../../api/teachers';
import { getCourse } from '../../api/courses';
import { extractErrorMessage } from '../../utils/api';
import { useI18n } from '../../i18n/I18nContext';
import LoadingState from '../../components/LoadingState/LoadingState';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import { formatDate } from '../../utils/datetime';
import './Teachers.css';

function TeachersList() {
  const { t } = useI18n();
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [removeModal, setRemoveModal] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const [courseData, teachersData] = await Promise.all([
          getCourse(courseId),
          getTeachersForCourse(courseId),
        ]);

        if (!cancelled) {
          setCourse(courseData);
          setTeachers(teachersData || []);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(extractErrorMessage(err, t('teacherAdmin.list.loadFailed')));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, retryTick]);

  const handleRetryLoad = useCallback(() => setRetryTick((n) => n + 1), []);

  const handleRemoveClick = (teacher) => {
    setError('');
    setRemoveModal(teacher);
  };

  const handleCancelRemove = () => {
    setError('');
    setRemoveModal(null);
  };

  const handleConfirmRemove = async () => {
    if (!removeModal) return;

    setRemoving(true);
    setError('');

    try {
      await removeTeacherFromCourse(courseId, removeModal.id);
      setTeachers(teachers.filter((tc) => tc.id !== removeModal.id));
      setRemoveModal(null);
    } catch (err) {
      setError(extractErrorMessage(err, t('teacherAdmin.list.removeFailed')));
    } finally {
      setRemoving(false);
    }
  };

  const filteredTeachers = search
    ? teachers.filter(
        (tc) =>
          (tc.name || '').toLowerCase().includes(search.toLowerCase()) ||
          (tc.email || '').toLowerCase().includes(search.toLowerCase()) ||
          (tc.phone || '').toLowerCase().includes(search.toLowerCase())
      )
    : teachers;

  if (loading) {
    return (
      <div className="teachers-page">
        <h1>{t('teacherAdmin.list.pageTitle')}</h1>
        <LoadingState skeleton="cards" count={3} />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="teachers-page">
        <h1>{t('teacherAdmin.list.pageTitle')}</h1>
        <PageEmptyState
          variant="error"
          title={t('teacherAdmin.list.loadFailed')}
          description={loadError !== t('teacherAdmin.list.loadFailed') ? loadError : undefined}
          actionLabel={t('teacherAdmin.list.retry')}
          onAction={handleRetryLoad}
        />
      </div>
    );
  }

  return (
    <div className="teachers-page">
      <div className="teachers-header">
        <h1>{t('teacherAdmin.list.pageTitleWithCourse', { course: course?.title })}</h1>
        <div className="teachers-header-actions">
          <button
            type="button"
            onClick={() => navigate(`/courses/${courseId}/invite-teacher`)}
            className="teachers-primary-btn"
          >
            {t('teacherAdmin.list.inviteTeacher')}
          </button>
        </div>
      </div>

      <div className="teachers-content">
        <div className="teachers-controls">
          <input
            type="text"
            className="teachers-search-input"
            placeholder={t('teacherAdmin.list.searchPlaceholder')}
            aria-label={t('teacherAdmin.list.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className="teachers-result-count">
            {t('teacherAdmin.list.resultCount', { filtered: filteredTeachers.length, total: teachers.length })}
          </span>
        </div>

        <div className="teachers-grid">
          {filteredTeachers.length === 0 ? (
            <PageEmptyState
              variant={search ? 'search' : 'students'}
              title={search ? t('teacherAdmin.list.noMatchTitle') : t('teacherAdmin.list.noTeachersTitle')}
              actionLabel={search ? t('teacherAdmin.list.clearSearch') : t('teacherAdmin.list.inviteFirstTeacher')}
              onAction={search ? () => setSearch('') : () => navigate(`/courses/${courseId}/invite-teacher`)}
            />
          ) : (
            filteredTeachers.map((teacher) => (
              <div key={teacher.id} className="teacher-card">
                {teacher.avatar ? (
                  <img
                    src={teacher.avatar}
                    alt={teacher.name}
                    className="teacher-card-avatar"
                  />
                ) : (
                  <span className="teacher-card-avatar-fallback" aria-hidden="true">
                    {(teacher.name || '?').trim().charAt(0).toUpperCase()}
                  </span>
                )}
                <h3>{teacher.name}</h3>

                <div className="teacher-card-contact">
                  {teacher.email && <p>{teacher.email}</p>}
                  {teacher.phone && <p>{teacher.phone}</p>}
                </div>

                <div className="teacher-card-stats">
                  <div className="stat">
                    <span className="teachers-stat-value">{teacher.studentsAssigned || 0}</span>
                    <span className="teachers-stat-label">{t('teacherAdmin.list.students')}</span>
                  </div>
                  <div className="stat">
                    <span className="teachers-stat-value">
                      {teacher.messagesSentLast7Days || 0}
                    </span>
                    <span className="teachers-stat-label">{t('teacherAdmin.list.messages')}</span>
                  </div>
                </div>

                {teacher.lastActiveAt && (
                  <div className="teacher-card-meta">
                    {t('teacherAdmin.list.lastActive', {
                      date: formatDate(teacher.lastActiveAt),
                    })}
                  </div>
                )}

                <div className="teacher-card-actions">
                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        `/courses/${courseId}/teachers/${teacher.id}/assign`
                      )
                    }
                    className="ds-btn ds-btn-secondary ds-btn-sm teacher-card-btn"
                  >
                    {t('teacherAdmin.list.assignStudents')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemoveClick(teacher)}
                    className="ds-btn ds-btn-danger ds-btn-sm teacher-card-btn"
                  >
                    {t('teacherAdmin.list.remove')}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {removeModal && (
        <div className="teachers-modal-overlay modal-overlay" onClick={handleCancelRemove}>
          <div className="teachers-modal-content modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{t('teacherAdmin.list.removeModalTitle')}</h3>
            <p>
              {t('teacherAdmin.list.removeModalBodyPrefix')}
              <strong>{removeModal.name}</strong>
              {t('teacherAdmin.list.removeModalBodySuffix')}
            </p>
            {error && <div className="teachers-error teachers-modal-error">{error}</div>}
            <div className="teachers-modal-actions">
              <button
                type="button"
                onClick={handleCancelRemove}
                className="ds-btn ds-btn-secondary"
                disabled={removing}
              >
                {t('teacherAdmin.list.cancel')}
              </button>
              <button
                type="button"
                onClick={handleConfirmRemove}
                className="ds-btn ds-btn-danger"
                disabled={removing}
              >
                {removing ? t('teacherAdmin.list.removing') : t('teacherAdmin.list.confirmRemove')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeachersList;
